import { Controller } from '@hotwired/stimulus'
import { dispatchCustomEvent } from '../../../helpers/custom_events_helper'

export default class extends Controller {
  static targets = ['frame', 'body', 'title']
  static values = {
    autoOpen: Boolean,
    autoOpenUrl: String,
    currentUrl: String
  }

  connect() {
    this.frameLoadHandler = this.handleFrameLoad.bind(this)
    this.frameTarget.addEventListener('turbo:frame-load', this.frameLoadHandler)

    this.handleKeydown = this.handleKeydown.bind(this)
    document.addEventListener('keydown', this.handleKeydown)

    // If already open (auto_open via server), just load the URL without animation
    if (this.autoOpenValue && this.hasAutoOpenUrlValue) {
      const isAlreadyOpen = this.element.classList.contains('components--ui--side-bar--open')
      if (isAlreadyOpen) {
        // Already open, just load content
        this.currentUrlValue = new URL(this.autoOpenUrlValue, window.location.origin).href

        // Show loading overlay
        this.frameTarget.setAttribute('aria-busy', 'true')
        this.frameTarget.classList.add('turbo-frame-loading')

        this.frameTarget.src = this.autoOpenUrlValue
      } else {
        // Not open yet, animate it open
        this.openWithUrl(this.autoOpenUrlValue)
      }
    }
  }

  disconnect() {
    if (this.frameTarget && this.frameLoadHandler) {
      this.frameTarget.removeEventListener('turbo:frame-load', this.frameLoadHandler)
    }

    document.removeEventListener('keydown', this.handleKeydown)
  }

  handleKeydown(event) {
    const isOpen = this.element.classList.contains('components--ui--side-bar--open')

    if (event.key === 'Escape' && isOpen) {
      this.close()
    }
  }

  openWithUrl(url, title = null) {
    if (title && this.hasTitleTarget) {
      this.titleTarget.textContent = title
    }
    // Store the URL we're loading
    this.currentUrlValue = new URL(url, window.location.origin).href

    // Show loading overlay
    this.frameTarget.setAttribute('aria-busy', 'true')
    this.frameTarget.classList.add('turbo-frame-loading')

    this.frameTarget.src = url
    this.open()
  }

  handleFrameLoad(event) {
    // Remove loading state
    this.frameTarget.removeAttribute('aria-busy')
    this.frameTarget.classList.remove('turbo-frame-loading')
  }

  toggle(url = null, title = null) {
    const isOpen = this.element.classList.contains('components--ui--side-bar--open')

    if (isOpen) {
      // If already open, check if we're clicking the same content
      if (url) {
        const newUrl = new URL(url, window.location.origin).href

        if (this.currentUrlValue === newUrl) {
          // Same content, close the sidebar
          this.close()
        } else {
          // Different content, update it
          this.updateContent(url, title)
        }
      } else {
        this.close()
      }
    } else {
      if (url) {
        this.openWithUrl(url, title)
      } else {
        this.open()
      }
    }
  }

  updateContent(url, title = null) {
    // Update title immediately
    if (title && this.hasTitleTarget) {
      this.titleTarget.textContent = title
    }

    // Store the URL we're loading
    this.currentUrlValue = new URL(url, window.location.origin).href

    // Show loading overlay without destroying DOM
    this.frameTarget.setAttribute('aria-busy', 'true')
    this.frameTarget.classList.add('turbo-frame-loading')

    // Load new content
    this.frameTarget.src = url

    dispatchCustomEvent(window, 'galette:sidebar:update-content', {
      detail: { url: url }
    })
  }

  open() {
    this.element.classList.add('components--ui--side-bar--open')

    dispatchCustomEvent(window, 'galette:sidebar:open', {
      detail: { url: this.currentUrlValue }
    })
  }

  close() {
    // Hide content immediately to prevent squishing during animation
    if (this.hasBodyTarget) {
      this.bodyTarget.style.opacity = '0'
    }

    // Remove open class to trigger width animation
    this.element.classList.remove('components--ui--side-bar--open')

    // Clear current URL
    this.currentUrlValue = ''

    // Emit custom event for other controllers to handle
    dispatchCustomEvent(window, 'galette:sidebar:close')

    // Clear frame content after animation completes
    setTimeout(() => {
      this.frameTarget.removeAttribute('src')
      this.frameTarget.innerHTML = ''
      // Reset opacity for next open
      if (this.hasBodyTarget) {
        this.bodyTarget.style.opacity = ''
      }
    }, 500)
  }
}
